import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import {
  useGetSettlementRunSummaryQuery,
  useGetSettlementLinesQuery,
  useGetSettlementBlockedActorsQuery,
  useGetSettlementPreviewPeriodQuery,
  useLazyExportSettlementCsvQuery,
} from '@/services/SettlementsService';
import {
  SettlementBlockedActorItem,
  SettlementBlockedActorsListParams,
  SettlementPreviewAllocatedByRun,
  SettlementPreviewPeriodParams,
  SettlementLineItem,
  SettlementLinesListParams,
  SettlementRunItem,
} from '@/types/settlements.types';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

enum SettlementKycStatus {
  NOT_REQUIRED = 'NOT_REQUIRED',
  PENDING = 'PENDING',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  ON_HOLD = 'ON_HOLD',
}

const kycStatusLabels: Record<SettlementKycStatus, string> = {
  [SettlementKycStatus.NOT_REQUIRED]: 'Not required',
  [SettlementKycStatus.PENDING]: 'Pending',
  [SettlementKycStatus.PASSED]: 'Passed',
  [SettlementKycStatus.FAILED]: 'Failed',
  [SettlementKycStatus.ON_HOLD]: 'On hold',
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'FINALIZED':
      return 'badge-success';
    case 'FAILED':
      return 'badge-danger';
    case 'RUNNING':
      return 'badge-warning';
    default:
      return 'badge-secondary';
  }
};

const getKycBadgeClass = (status: string) => {
  switch (status) {
    case 'PASSED':
    case 'NOT_REQUIRED':
      return 'badge-success';
    case 'PENDING':
      return 'badge-warning';
    case 'FAILED':
    case 'ON_HOLD':
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
};

const getPayoutExplanation = (line: SettlementLineItem): string | null => {
  if (line.payout_status === 'ELIGIBLE') {
    if (line.carry_forward_amount > 0) {
      return 'Includes previously carried-forward balance.';
    }
    return null;
  }

  if (
    line.kyc_status_at_run === 'FAILED' ||
    line.kyc_status_at_run === 'ON_HOLD'
  ) {
    return 'Excluded from payout due to KYC status at run time.';
  }

  if (line.kyc_status_at_run === 'PENDING') {
    return 'Pending KYC – automated payouts are disabled until KYC is completed.';
  }

  if (line.payout_status === 'CARRIED_FORWARD') {
    return 'Net amount carried forward because it is below the minimum payout threshold or negative.';
  }

  return null;
};

const SettlementDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [blockedActorsPagination, setBlockedActorsPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const [blockedActorsTotalPages, setBlockedActorsTotalPages] = useState(1);
  const [blockedActorsTotalItems, setBlockedActorsTotalItems] = useState(0);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useGetSettlementRunSummaryQuery(id || '', { skip: !id });

  const previewParams: SettlementPreviewPeriodParams | undefined = summary
    ? {
        period_start: summary.period_start,
        period_end: summary.period_end,
      }
    : undefined;

  const {
    data: preview,
    isFetching: previewLoading,
    error: previewError,
  } = useGetSettlementPreviewPeriodQuery(
    previewParams as SettlementPreviewPeriodParams,
    {
      skip: !previewParams,
    }
  );

  const listParams: SettlementLinesListParams | undefined = id
    ? {
        id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      }
    : undefined;

  const {
    data: lines,
    isFetching: linesLoading,
    error: linesError,
  } = useGetSettlementLinesQuery(listParams as SettlementLinesListParams, {
    skip: !listParams,
  });

  const blockedActorsParams: SettlementBlockedActorsListParams | undefined = id
    ? {
        id,
        page: blockedActorsPagination.pageIndex + 1,
        limit: blockedActorsPagination.pageSize,
      }
    : undefined;

  const {
    data: blockedActors,
    isFetching: blockedActorsLoading,
    error: blockedActorsError,
  } = useGetSettlementBlockedActorsQuery(
    blockedActorsParams as SettlementBlockedActorsListParams,
    {
      skip: !blockedActorsParams,
    }
  );

  const [exportCsv, { isLoading: isExporting }] =
    useLazyExportSettlementCsvQuery();

  const rows: SettlementLineItem[] = useMemo(() => lines?.items ?? [], [lines]);

  useEffect(() => {
    if (lines) {
      setTotalPages(lines.totalPages || 1);
      setTotalItems(lines.totalItems || 0);
    }
  }, [lines]);

  useEffect(() => {
    if (blockedActors) {
      setBlockedActorsTotalPages(blockedActors.totalPages || 1);
      setBlockedActorsTotalItems(blockedActors.totalItems || 0);
    }
  }, [blockedActors]);

  useEffect(() => {
    const err = (summaryError || linesError) as
      | { data?: unknown; error?: string }
      | null
      | undefined;
    if (!err) return;
    let message = 'Failed to fetch settlement details';
    if (
      err.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [summaryError, linesError, enqueueSnackbar]);

  useEffect(() => {
    const err = blockedActorsError as
      | { data?: unknown; error?: string }
      | null
      | undefined;
    if (!err) return;
    let message = 'Failed to fetch blocked actors';
    if (
      err.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [blockedActorsError, enqueueSnackbar]);

  useEffect(() => {
    const err = previewError as
      | { data?: unknown; error?: string }
      | null
      | undefined;
    if (!err) return;
    let message = 'Failed to fetch preview period';
    if (
      err.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [previewError, enqueueSnackbar]);

  const eligibleRows = rows.filter((r) => r.payout_status === 'ELIGIBLE');
  const blockedRows = rows.filter((r) => r.payout_status !== 'ELIGIBLE');

  const baseColumns = [
    {
      accessorFn: (row: SettlementLineItem) => row.actor_type,
      id: 'actor_type',
      header: 'Actor Type',
      enableSorting: false,
    },
    {
      accessorFn: (row: SettlementLineItem) => row.actor?.name ?? row.actor_id,
      id: 'actor',
      header: 'Actor',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => {
        const actor = row.original.actor;
        const name = actor?.name ?? row.original.actor_id;
        return (
          <div className="flex flex-col">
            <span className="font-medium break-words">{name}</span>
            {actor?.email && (
              <span className="text-xs text-gray-500 break-all">
                {actor.email}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row: SettlementLineItem) => row.transaction_count,
      id: 'transactions',
      header: 'Transactions',
      enableSorting: false,
    },
    {
      accessorFn: (row: SettlementLineItem) => row.gross_total,
      id: 'gross_total',
      header: 'Gross Total',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.gross_total)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.vat_total,
      id: 'vat_total',
      header: 'VAT Total',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.vat_total)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.commission_total,
      id: 'commission_total',
      header: 'Commission (Saaristo)',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.commission_total)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.carry_forward_amount,
      id: 'carry_forward_amount',
      header: 'Carry Forward',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.carry_forward_amount)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.psp_fee_total,
      id: 'psp_fee_total',
      header: 'PSP Fee',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.psp_fee_total)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.payout_amount,
      id: 'payout_amount',
      header: 'Payout Amount',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span>{formatFinnishCurrency(row.original.payout_amount)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementLineItem) => row.currency,
      id: 'currency',
      header: 'Currency',
      enableSorting: false,
    },
    {
      accessorFn: (row: SettlementLineItem) => row.kyc_status_at_run,
      id: 'kyc_status',
      header: 'KYC Status at Run',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => (
        <span
          className={`badge ${getKycBadgeClass(row.original.kyc_status_at_run)}`}
        >
          {kycStatusLabels[
            row.original.kyc_status_at_run as SettlementKycStatus
          ] ?? row.original.kyc_status_at_run}
        </span>
      ),
    },
  ];

  const allocatedByRunRows: SettlementPreviewAllocatedByRun[] = useMemo(
    () => preview?.allocated_by_run ?? [],
    [preview]
  );

  const blockedExtraColumns = [
    {
      accessorFn: (row: SettlementLineItem) => row.payout_status,
      id: 'payout_status',
      header: 'Payout Status',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementLineItem } }) => {
        const status = row.original.payout_status;
        // Replace underscores with spaces and format for display
        const formattedStatus = status ? status.replace(/_/g, ' ') : '';
        return <span>{formattedStatus}</span>;
      },
    },
    {
      accessorFn: (row: SettlementLineItem) => getPayoutExplanation(row) ?? '',
      id: 'explanation',
      header: 'Explanation',
      enableSorting: false,
    },
  ];

  const blockedColumns = [...baseColumns, ...blockedExtraColumns];

  const blockedActorsColumns = [
    {
      accessorFn: (row: SettlementBlockedActorItem) => row.actor?.type,
      id: 'actor_type',
      header: 'Actor Type',
      enableSorting: false,
    },
    {
      accessorFn: (row: SettlementBlockedActorItem) => row.actor?.name,
      id: 'actor',
      header: 'Actor',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementBlockedActorItem } }) => {
        const actor = row.original.actor;
        return (
          <div className="flex flex-col">
            <span className="font-medium break-words">{actor?.name}</span>
            {actor?.email && (
              <span className="text-xs text-gray-500 break-all">
                {actor.email}
              </span>
            )}
            {actor?.location && (
              <span className="text-xs text-gray-500 break-words">
                {actor.location}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row: SettlementBlockedActorItem) => row.kyc_status,
      id: 'kyc_status',
      header: 'KYC Status',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementBlockedActorItem } }) => (
        <span className={`badge ${getKycBadgeClass(row.original.kyc_status)}`}>
          {kycStatusLabels[row.original.kyc_status as SettlementKycStatus] ??
            row.original.kyc_status}
        </span>
      ),
    },
    {
      accessorFn: (row: SettlementBlockedActorItem) => row.reason,
      id: 'reason',
      header: 'Reason',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementBlockedActorItem } }) => (
        <span className="break-words">{row.original.reason}</span>
      ),
    },
  ];

  const handleExportCsv = async () => {
    if (!id) return;
    try {
      const blob = await exportCsv(id).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `settlement_${id}_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Settlement CSV exported successfully', {
        variant: 'success',
      });
    } catch (e) {
      let message = 'Failed to export settlement CSV';
      if (
        e &&
        typeof e === 'object' &&
        'data' in (e as Record<string, unknown>)
      ) {
        const data = (e as { data?: { message?: string } }).data;
        if (data?.message) message = data.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (summaryLoading && !summary) {
    return (
      <Container>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="loading loading-spinner loading-lg mb-4" />
            <p className="text-gray-500">Loading settlement details...</p>
          </div>
        </div>
      </Container>
    );
  }

  if (!summary) {
    return (
      <Container>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <KeenIcon
              icon="information-2"
              className="text-4xl text-gray-400 mb-4"
            />
            <p className="text-gray-500">Settlement run not found</p>
            <button
              onClick={() => navigate('/settlements')}
              className="btn btn-sm btn-primary mt-4"
            >
              Back to Settlements
            </button>
          </div>
        </div>
      </Container>
    );
  }

  const s: SettlementRunItem = summary;

  return (
    <Container>
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/settlements')}
          className="btn btn-sm btn-ghost gap-2"
        >
          <KeenIcon icon="arrow-left" className="text-lg" />
          Back to Settlements
        </button>
        <button
          className="btn btn-sm btn-primary flex items-center gap-2"
          onClick={handleExportCsv}
          disabled={isExporting}
        >
          <KeenIcon type="solid" icon="export" className="text-white" />
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold mb-2">Settlement Run</h1>
              <p className="text-sm text-gray-500">
                Period{' '}
                {s.period_start ? formatDisplayDate(s.period_start, '') : ''} -{' '}
                {s.period_end ? formatDisplayDate(s.period_end, '') : ''}
              </p>
              <p className="text-sm text-gray-500">
                Created{' '}
                {s.created_at ? formatDisplayDateTime(s.created_at) : ''}
              </p>
            </div>
            <span className={`badge badge-lg ${getStatusBadgeClass(s.status)}`}>
              {s.status}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Actors</p>
              <p className="text-xl font-semibold">{s.actor_count}</p>
            </div>
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Total Net Payable</p>
              <p className="text-xl font-semibold">
                {formatFinnishCurrency(s.total_net_payable)}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Min Payout Threshold</p>
              <p className="text-xl font-semibold">
                {typeof s.config_snapshot?.min_payout_threshold_eur === 'number'
                  ? formatFinnishCurrency(
                      s.config_snapshot.min_payout_threshold_eur
                    )
                  : '—'}
              </p>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p>
              Settlement respects configured payout rules: amounts below the
              minimum payout threshold or negative balances are carried forward
              instead of being paid out.
            </p>
            <p className="mt-1">
              KYC/AML enforcement: actors with FAILED or ON_HOLD KYC are
              excluded from payouts; PENDING behaviour follows configuration and
              is shown below.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="card-title">Preview Period</h2>
                <p className="text-sm text-gray-500">
                  Uses the current settlement run period.
                </p>
              </div>
              {previewLoading && (
                <span className="text-sm text-gray-500">Loading...</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-base-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Candidate Total</p>
                <p className="text-xl font-semibold">
                  {typeof preview?.candidate_total === 'number'
                    ? preview.candidate_total
                    : '—'}
                </p>
              </div>
              <div className="bg-base-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Allocated Total</p>
                <p className="text-xl font-semibold">
                  {typeof preview?.allocated_total === 'number'
                    ? preview.allocated_total
                    : '—'}
                </p>
              </div>
              <div className="bg-base-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Unallocated Total</p>
                <p className="text-xl font-semibold">
                  {typeof preview?.unallocated_total === 'number'
                    ? preview.unallocated_total
                    : '—'}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Allocated by Run</h3>
                <p className="text-sm text-gray-500">
                  {allocatedByRunRows.length} run(s)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Settlement Run ID</th>
                      <th>Transaction Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewLoading ? (
                      <tr>
                        <td colSpan={2} className="text-center text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    ) : allocatedByRunRows.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center text-gray-500">
                          No allocations found for this period.
                        </td>
                      </tr>
                    ) : (
                      allocatedByRunRows.map((row) => (
                        <tr key={row.settlement_run_id}>
                          <td className="break-all">{row.settlement_run_id}</td>
                          <td>{row.transaction_count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <h2 className="card-title">Payout-eligible actors</h2>
              <p className="text-sm text-gray-500">
                {eligibleRows.length} actors eligible for payout.
              </p>
            </div>
            <DataGrid
              columns={baseColumns}
              data={eligibleRows}
              rowSelect={false}
              loading={linesLoading}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: '{from} - {to} of {count}',
                sizes: [10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              serverSide={true}
              aria-label="Settlement lines table"
            />
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <h2 className="card-title">Blocked Actors</h2>
              <p className="text-sm text-gray-500">
                {blockedActorsTotalItems} blocked actors.
              </p>
            </div>
            <DataGrid
              columns={blockedActorsColumns}
              data={blockedActors?.items ?? []}
              rowSelect={false}
              loading={blockedActorsLoading}
              pagination={{
                page: blockedActorsPagination.pageIndex,
                size: blockedActorsPagination.pageSize,
                more: true,
                totalItems: blockedActorsTotalItems,
                totalPages: blockedActorsTotalPages,
                setPagination: setBlockedActorsPagination,
                info: '{from} - {to} of {count}',
                sizes: [10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              serverSide={true}
              aria-label="Blocked actors table"
            />
          </div>
        </div>
        {blockedRows.length > 0 && (
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <h2 className="card-title">Blocked / carried-forward actors</h2>
                <p className="text-sm text-gray-500">
                  {blockedRows.length} actors excluded from payout or carried
                  forward.
                </p>
              </div>
              <DataGrid
                columns={blockedColumns}
                data={blockedRows}
                rowSelect={false}
                loading={linesLoading}
                pagination={undefined}
                serverSide={false}
                aria-label="Blocked settlement lines table"
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default SettlementDetails;
